import { useState } from 'react';

import chapterImg2 from '../../../../assets/images/courses/collaboration-fundamentals/chapter2.2.1.jpg';
import introImg from '../../../../assets/images/courses/collaboration-fundamentals/chapter2.2.jpg';
import chapterImg1 from '../../../../assets/images/courses/collaboration-fundamentals/chapter2.3.jpg';
import { CourseChapter } from '../../../../courses/courses.types';
import { CustomAccordion } from '../../../common/Accordion';
import { MediaBlock } from '../../MediaBlock';
import { ToggleCard } from '../../ToggleCard';

interface CollaborationFundamentalsProps {
  chapter: CourseChapter;
  onNextChapter: (nextIndex: number) => void;
  totalPages: number;
  courseId?: string;
  onQuizResult?: (result: boolean | undefined) => void;
}

export const CollaborationFundamentalsChapter2: React.FC<CollaborationFundamentalsProps> = ({
  chapter,
  onNextChapter,
  totalPages,
  // courseId,
}) => {
  const [openCardPosition, setOpenCardPosition] = useState<string | null>(null);
  const handleNextChapterClick = () => {
    onNextChapter(chapter.index + 1);
  };
  const handlePrint = () => {
    const printArea = document.getElementById('print-view');
    if (printArea) {
      printArea.classList.add('print-view');

      const cleanup = () => {
        printArea.classList.remove('print-view');
        window.onafterprint = null;
      };

      window.onafterprint = cleanup;

      window.print();
    }
  };

  const handleToggleCard = (position: string) => {
    setOpenCardPosition((prevPosition) => (prevPosition === position ? null : position));
  };
  const accordionData1 = [
    {
      id: 'panel1',
      title: 'Teamwork Makes the Dream Work',
      content:
        'When you collaborate, everyone brings their unique skills and ideas. This can make projects more creative and effective.',
    },
    {
      id: 'panel2',
      title: 'Learning from Each Other',
      content:
        'Working with others allows you to learn new things and get better at stuff you’re not so good at. It’s like having multiple teachers!',
    },
    {
      id: 'panel3',
      title: 'Sharing the Load',
      content:
        'Collaboration means you don’t have to do everything by yourself. This can make big tasks more manageable and less stressful.',
    },
    {
      id: 'panel4',
      title: 'Building Friendships',
      content:
        'Working together helps you build strong relationships with your classmates. This can make school more fun and enjoyable.',
    },
    {
      id: 'panel5',
      title: 'Achieving Common Goals',
      content:
        'When everyone works together, you can achieve goals more quickly and effectively. It’s satisfying to see what you can accomplish as a team.',
    },
  ];

  const accordionData2 = [
    {
      id: 'panel1',
      title: 'Lack of Communication',
      content:
        'If team members don’t talk to each other, important details can be missed, and misunderstandings can happen. This can lead to mistakes and frustration.',
    },
    {
      id: 'panel2',
      title: 'Unequal Participation',
      content:
        'Sometimes, not everyone puts in the same amount of effort. This can make others feel annoyed or unfairly burdened with too much work.',
    },
    {
      id: 'panel3',
      title: 'Conflicting Ideas',
      content:
        'Differences in opinions can sometimes lead to arguments. If not handled well, this can create tension and slow down progress.',
    },
    {
      id: 'panel4',
      title: 'Lack of Organization',
      content:
        'Without clear roles and a plan, collaboration can become chaotic. People might not know what they’re supposed to do, leading to confusion and wasted time.',
    },
    {
      id: 'panel5',
      title: 'Unmet Expectations',
      content:
        'If team members have different expectations about the outcome or process, it can lead to disappointment and conflicts when the project doesn’t turn out as expected.',
    },
  ];

  return (
    <>
      <div className="content-wrap lg:border-l lg:pl-8 lg:pt-8 pt-4 pb-10">
        <p className="text-sm italic  mb-7">
          Part {chapter.index} of {totalPages}
        </p>
        <h2 className="font-semibold mt-7 text-3xl">What is Collaboration</h2>

        <MediaBlock mediaImage={introImg}></MediaBlock>
        <h3 className="text-2xl text-purple-600 font-bold mt-4 mb-1">What is Collaboration</h3>

        <p className="text-xl font-light flex-1 mt-5 mb-5">
          Collaboration refers to your ability to contribute and support others to achieve a common
          goal. Collaboration is when people work together. Here are some examples of collaboration:
        </p>
        <ul className="list-disc pl-5 mb-14 text-xl font-light">
          <li className="pl-9 mb-7">Parents cook dinner together</li>
          <li className="pl-9 mb-7">Co-workers build a house</li>
          <li className="pl-9 mb-7">Soccer players try to score a goal</li>
          <li className="pl-9 mb-7">Students do a group project</li>
        </ul>
        <p className="text-xl font-light flex-1 mt-5 mb-5">
          For example, at work we use this skill to provide meaningful support to team members while
          completing a project.
        </p>
        <p className="text-2xl  mt-7 mb-7 leading-10">Collaboration includes ...</p>
        <div className="md:flex items-center mb-7">
          <div className="md:mr-8 max-w-full md:max-w-xs w-full mb-5 md:mb-0">
            <img src={chapterImg1} alt="" />
          </div>
          <p className="flex-1">
            <br />
            <br />
            ... a combination of who, if anyone, you work with to complete a task and if and how
            much of a leadership or supervisory role you take in getting the task completed. For
            example, musicians work on a team to rehearse for a gig, and you might lead a team
            working on a class project.
          </p>
        </div>
        <p className="text-xl font-light flex-1 mt-5 mb-5">
          For example, at work we use this skill to provide meaningful support to team members while
          completing a project.
        </p>
        <ol className=" list-decimal pl-7 mb-14 text-xl font-light">
          <li className="pl-9 mb-7">Asking questions</li>
          <li className="pl-9 mb-7">Answering questions</li>
          <li className="pl-9 mb-7">Asking for help</li>
          <li className="pl-9 mb-7">Helping a co-worker</li>
        </ol>
        <p className="text-xl text-center font-light mb-8">
          Click on the markers for some specific examples of actions that show collaboration:
        </p>
        <div>
          <div className="mb-20 mt-10">
            <div className="w-[250px] sm:w-[500px] md:w-[635px]  mx-auto relative">
              <img className="w-full" src={chapterImg2} alt="" />
              <div className="absolute top-[3%] sm:top-[10%] left-[10%]">
                <ToggleCard
                  position="top-left"
                  isOpen={openCardPosition === 'top-left'}
                  onToggle={handleToggleCard}
                  cardContent={
                    <div>
                      <p className="text-md sm:text-xl font-light mb-0 sm:mb-8">
                        Get together and brainstorm ideas as a team to come up with creative
                        solutions.
                      </p>
                    </div>
                  }
                />
              </div>
              <div className="absolute top-[-10%] sm:top-[-4%] left-[34%]">
                <ToggleCard
                  position="top-left-1"
                  isOpen={openCardPosition === 'top-left-1'}
                  onToggle={handleToggleCard}
                  cardContent={
                    <div>
                      <p className="text-md sm:text-xl font-light mb-0 sm:mb-8">
                        Use apps like Discord, Google Docs, or Trello to communicate and manage
                        group projects.
                      </p>
                    </div>
                  }
                />
              </div>
              <div className="absolute top-[-10%] sm:top-[-5%] left-[60%]">
                <ToggleCard
                  position="top"
                  isOpen={openCardPosition === 'top'}
                  onToggle={handleToggleCard}
                  cardContent={
                    <div>
                      <p className="text-md sm:text-xl font-light mb-0 sm:mb-8">
                        Work on projects together, sharing responsibilities and helping each other
                        out.
                      </p>
                    </div>
                  }
                />
              </div>
              <div className="absolute top-[5%] right-[0%] sm:right-[7%]">
                <ToggleCard
                  position="top-right"
                  isOpen={openCardPosition === 'top-right'}
                  onToggle={handleToggleCard}
                  cardContent={
                    <div>
                      <p className="text-md sm:text-xl font-light mb-0 sm:mb-8">
                        Have regular group chats or meetings to discuss what everyone is working on
                        and solve problems together.
                      </p>
                    </div>
                  }
                />
              </div>
              <div className="absolute top-[30%] sm:top-[35%] right-[-11%] sm:right-[-5%]">
                <ToggleCard
                  position="top-right-1"
                  isOpen={openCardPosition === 'top-right-1'}
                  onToggle={handleToggleCard}
                  cardContent={
                    <div>
                      <p className="text-md sm:text-xl font-light mb-0 sm:mb-8">
                        Make decisions as a group, making sure everyone’s ideas are heard and
                        considered.
                      </p>
                    </div>
                  }
                />
              </div>
              <div className="absolute top-[60%] right-[-10%] sm:right-[-5%]">
                <ToggleCard
                  position="right"
                  isOpen={openCardPosition === 'right'}
                  onToggle={handleToggleCard}
                  cardContent={
                    <div>
                      <p className="text-md sm:text-xl font-light mb-0 sm:mb-8">
                        Create a safe space where everyone feels comfortable sharing their ideas and
                        opinions.
                      </p>
                    </div>
                  }
                />
              </div>
              <div className="absolute bottom-[3%] sm:bottom-[7%] right-[2%] sm:right-[5%]">
                <ToggleCard
                  position="bottom-right"
                  isOpen={openCardPosition === 'bottom-right'}
                  onToggle={handleToggleCard}
                  cardContent={
                    <div>
                      <p className="text-md sm:text-xl font-light mb-0 sm:mb-8">
                        Participate in team-building activities, like sports or group games, to
                        build trust and improve communication.
                      </p>
                    </div>
                  }
                />
              </div>
              <div className="absolute bottom-[-12%] sm:bottom-[-5%] right-[25%] sm:right-[30%]">
                <ToggleCard
                  position="bottom-right-1"
                  isOpen={openCardPosition === 'bottom-right-1'}
                  onToggle={handleToggleCard}
                  cardContent={
                    <div>
                      <p className="text-md sm:text-xl font-light mb-0 sm:mb-8">
                        Pair up with a friend or classmate who’s good at something you want to
                        learn, and help each other out.
                      </p>
                    </div>
                  }
                />
              </div>
              <div className="absolute bottom-[-12%] sm:bottom-[-5%] left-[30%] sm:left-[40%]">
                <ToggleCard
                  position="bottom"
                  isOpen={openCardPosition === 'bottom'}
                  onToggle={handleToggleCard}
                  cardContent={
                    <div>
                      <p className="text-md sm:text-xl font-light mb-0 sm:mb-8">
                        Share your skills and knowledge with your friends, like teaching someone how
                        to use a new app or tool.
                      </p>
                    </div>
                  }
                />
              </div>
              <div className="absolute bottom-[2%] sm:bottom-[5%] left-[5%] sm:left-[9%]">
                <ToggleCard
                  position="bottom-left"
                  isOpen={openCardPosition === 'bottom-left'}
                  onToggle={handleToggleCard}
                  cardContent={
                    <div>
                      <p className="text-md sm:text-xl font-light mb-0 sm:mb-8">
                        Give each other constructive feedback on assignments to help everyone
                        improve.
                      </p>
                    </div>
                  }
                />
              </div>
              <div className="absolute bottom-[30%] left-[-7%] sm:left-[-4%]">
                <ToggleCard
                  position="bottom-left-1"
                  isOpen={openCardPosition === 'bottom-left-1'}
                  onToggle={handleToggleCard}
                  cardContent={
                    <div>
                      <p className="text-md sm:text-xl font-light mb-0 sm:mb-8">
                        {' '}
                        Agree on shared goals so everyone knows what they’re working towards.
                      </p>
                    </div>
                  }
                />
              </div>
              <div className="absolute bottom-[60%] sm:bottom-[62%] right-[93%] sm:right-[100%]">
                <ToggleCard
                  position="left"
                  isOpen={openCardPosition === 'left'}
                  onToggle={handleToggleCard}
                  cardContent={
                    <div>
                      <p className="text-md sm:text-xl font-light mb-0 sm:mb-8">
                        Work with classmates from different groups or classes on projects to get new
                        perspectives.
                      </p>
                    </div>
                  }
                />
              </div>
            </div>
          </div>
        </div>

        <h3 className="text-2xl text-purple-600 font-bold m-5">Why is Collaboration Important</h3>
        <p className="text-xl text-center font-light mb-8">
          Collaboration is like a superpower for getting things done, especially in school. When you
          work together with your classmates, you can combine everyone’s strengths, learn from each
          other, and share the workload. It’s not just about finishing projects faster; it’s also
          about making them better and more creative. Plus, collaborating helps build friendships
          and makes school life more enjoyable. By working as a team, you can achieve common goals
          and feel proud of what you accomplish together.
        </p>
        <p className="text-l  font-light mb-8">Click the '+' icons to learn more.</p>
        {accordionData1.map((data) => (
          <CustomAccordion
            key={data.title}
            title={data.title}
            content={data.content}
            id={data.id}
          />
        ))}

        <h3 className="text-2xl text-purple-600 font-bold m-5">When Collaboration Goes Wrong</h3>
        <p className="text-xl text-center font-light mb-8">
          Sometimes, working with others doesn’t go as smoothly as planned. Lack of communication,
          unequal participation, and conflicting ideas can turn a great team project into a
          frustrating experience. Without clear roles and organization, collaboration can become
          chaotic, leading to mistakes and missed deadlines. When team members have different
          expectations, it can result in disappointment and tension. Understanding these pitfalls
          can help you avoid them and improve your teamwork skills.
        </p>

        <p className="text-l  font-light mb-8">Click the '+' icons to learn more.</p>
        {accordionData2.map((data) => (
          <CustomAccordion
            key={data.title}
            title={data.title}
            content={data.content}
            id={data.id}
          />
        ))}
        <h3 className="text-2xl text-purple-600 font-bold m-5">Reflect</h3>
        <p className="text-xl font-light flex-1 mt-5 mb-5">
          Think about your collaboration skills. Look at the list of collaboration skills below.
          Check the boxes that feel like you.
        </p>
        <ol className="list-decimal pl-5 mb-14 text-xl font-light">
          {[
            'I share ideas',
            'I help other people',
            'I work with people to get the job done',
            'I listen to other people´s ideas',
            'I don´t blame other people',
            'I try to solve problems',
            'I am reliable',
          ].map((item, index) => (
            <li key={index} className="pl-9 mb-7 flex items-center">
              <input type="checkbox" className="mr-3 font-normal w-6 h-6" /> {item}
            </li>
          ))}
        </ol>

        <h3 className="text-2xl text-purple-600 font-bold m-5">
          Activity: What Is and Isn't Collaboration
        </h3>
        <p className="text-xl font-light flex-1 mt-5 mb-5">
          Sort the statements below into what shows collaboration and what doesn’t show
          collaboration for each situation by dragging and dropping them into the correct column.
          Note: color statements correspond with color situations.
        </p>

        <div className="w-full overflow-x-auto px-2">
          {' '}
          <table className="seek-information min-w-full border-collapse">
            <thead>
              <tr>
                <th style={{ backgroundColor: 'rgb(144, 105, 222)', color: 'whitesmoke' }}>
                  Situation
                </th>
                <th style={{ backgroundColor: 'rgb(144, 105, 222)', color: 'whitesmoke' }}>
                  Collaboration
                </th>
                <th style={{ backgroundColor: 'rgb(144, 105, 222)', color: 'whitesmoke' }}>
                  Not collaboration
                </th>
              </tr>
            </thead>
            <tbody className="bg-purple-100">
              <tr>
                <td>
                  {' '}
                  <div className="bg-green-700 text-center py-5 px-5 w-full text-white ">
                    You promise to write a <br /> report for the team
                  </div>
                </td>
                <td></td>
                <td></td>
              </tr>
              <tr>
                <td>
                  <div className="bg-yellow-500 text-center py-5 px-5 w-full text-white ">
                    Your co-work is sick and goes home
                  </div>
                </td>
                <td></td>
                <td></td>
              </tr>
              <tr>
                <td>
                  <div className="bg-blue-500 text-center py-5 px-5 w-full text-white ">
                    You have a new co-worker
                  </div>
                </td>
                <td></td>
                <td></td>
              </tr>
              <tr>
                <td></td>
                <td></td>
                <td></td>
              </tr>
              <tr>
                <td></td>
                <td></td>
                <td></td>
              </tr>
              <tr>
                <td>
                  <div className="bg-green-700 text-center py-5 px-5 w-full text-white ">
                    You write the report for the team
                  </div>
                </td>
                <td>
                  <div className="bg-yellow-500 text-center py-5 px-5 w-full text-white ">
                    You help finish the extra work
                  </div>
                </td>
                <td>
                  <div className="bg-blue-500 text-center py-5 px-5 w-full text-white ">
                    You answer their questions
                  </div>
                </td>
              </tr>
              <tr>
                <td>
                  <div className="bg-green-700 text-center py-5 px-5 w-full text-white ">
                    You tell the boss it <br /> was your co-worker's job
                  </div>
                </td>
                <td>
                  <div className="bg-yellow-500 text-center py-5 px-5 w-full text-white ">
                    You let your other <br /> co-workers do all th extra work
                  </div>
                </td>
                <td>
                  <div className="bg-blue-500 text-center py-5 px-5 w-full text-white ">
                    You don’t talk to your new co-worker
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <h3 className="text-2xl text-purple-600 font-bold  m-5">
          Activity: Give it a Try - Think About Your Collaboration
        </h3>
        <p className="text-xl font-bold flex-1 mt-5 mb-5">Instructions</p>
        <ol className="list-decimal pl-7 mb-14 text-xl font-light">
          <li className="pl-9 mb-7">
            Think about how you use your Collaboration skills every day – whether at a job or in
            your daily life.
          </li>
          <li className="pl-9 mb-7">
            Complete the following table with an example of each of the work contexts given (alone
            or with a partner); be as specific as you can.
          </li>
          <li className="pl-9 mb-7">
            Think about ways in which you take responsibility – at work, school, or in your daily
            life. (Do you have brothers or sisters? A part time job? Are you on a team?) What
            examples can you give?
          </li>
        </ol>
        <p className="text-xl font-light flex-1 mt-5 mb-5">
          We’ve filled in an example below for a graphic designer’s workday to help you get started.
        </p>
        <div className="mt-7 mb-5">
          <h3 className="text-l font-semibold mb-5">Example: Graphic Designer</h3>
          <table className="seek-information">
            <thead>
              <tr>
                <th style={{ backgroundColor: 'blueviolet', color: 'whitesmoke' }}>Work Context</th>
                <th style={{ backgroundColor: 'blueviolet', color: 'whitesmoke' }}>Description</th>
              </tr>
            </thead>
            <tbody className="bg-purple-100">
              <tr>
                <td>
                  <div className=" text-lg font-bold ">Alone (or independently)</div>
                </td>
                <td>
                  Graphic designer working independently in home studio, creating preliminary
                  sketches for video game characters.
                </td>
              </tr>
              <tr>
                <td>
                  <div className=" text-lg font-bold ">
                    With a Partner (and/or as part of a team)
                  </div>
                </td>
                <td>
                  Brainstorm story lines with another person. Participate in online meetings each
                  morning with everyone working on developing the game.
                </td>
              </tr>
              <tr>
                <td>
                  <div className=" text-lg font-bold ">Responsibility</div>
                </td>
                <td>
                  Graphic designers may work in a lead or supervisory role, when they are part of a
                  team.
                </td>
              </tr>
            </tbody>
          </table>
          <p className="font-light text-lg mt-8 mb-8">
            Now it's your turn. Complete the chart below, you may print your responses upon
            completion:
          </p>
        </div>
        <div id="print-view">
          <table className="seek-information w-full">
            <thead>
              <tr>
                <th style={{ backgroundColor: 'blueviolet', color: 'whitesmoke' }}>Work Context</th>
                <th style={{ backgroundColor: 'blueviolet', color: 'whitesmoke' }}>Description</th>
              </tr>
            </thead>
            <tbody className="bg-purple-100">
              <tr>
                <td>
                  <div className=" text-lg font-bold ">Alone (or independently)</div>
                </td>
                <td>
                  <textarea
                    className="w-full h-32 p-2 border rounded"
                    placeholder="Enter your text here..."
                  ></textarea>
                </td>
              </tr>
              <tr>
                <td>
                  <div className=" text-lg font-bold ">
                    With a Partner (and/or as part of a team)
                  </div>
                </td>
                <td>
                  <textarea
                    className="w-full h-32 p-2 border rounded"
                    placeholder="Enter your text here..."
                  ></textarea>
                </td>
              </tr>
              <tr>
                <td>
                  <div className=" text-lg font-bold ">Responsibility</div>
                </td>
                <td>
                  <textarea
                    className="w-full h-32 p-2 border rounded"
                    placeholder="Enter your text here..."
                  ></textarea>
                </td>
              </tr>
            </tbody>
          </table>
          <div className="mt-5 text-right">
            <button onClick={handlePrint} className="btn-internal">
              PRINT
            </button>
          </div>
        </div>

        <h3 className="text-2xl mt-2  text-center font-light mb-5">
          Let's look at collaboration as a critical skill.
        </h3>
        <button onClick={handleNextChapterClick} className="btn-internal w-full">
          Collaboration - A Critical Skill
        </button>
      </div>
    </>
  );
};
